import ClearIcon from "@mui/icons-material/Clear";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { subMonths } from "date-fns";
import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import DatePicker from "react-datepicker";
import { useNavigate } from "react-router-dom";
import { Card, Input, FormGroup, Row, Col } from "reactstrap";

import { Btn, H5, MUIIcons, SecondaryButton } from "../../AbstractElements";
import LabelTooltip from "../../CommonElements/LabelTooltip";
import { useAppDispatch, useAppSelector } from "../../ReduxToolkit/Hooks";
import {
  getTrainerActivities,
  deleteTrainerActivity,
} from "../../ReduxToolkit/Reducers/ActivityTrackingSlice";
import {
  ActivityTo,
  Select,
  ActivityFrom,
  Search,
  Leave,
  Others,
  Travel,
  DocumentationWork,
  TrainingMaterialUpdate,
  TrainingMaterialPreparation,
  TrainingAidMaintenance,
  TrainingAidDevelopment,
  SelfLearning,
  ExpandAll,
  CollapseAll,
  Add,
  SearchAllItems,
  Description,
  DurationInMinutes,
  Action,
  CourseName,
  LeaveType,
  ActivityOn,
} from "../../utils/Constant";
import {
  activityCategoryMap,
  activityCategoryOptions,
  createTooltipCell,
  formatCustomDate,
  leaveTypes,
} from "../../utils/helper/helper";
import "./style.scss";

interface ExpandedCategories {
  selfLearning: boolean;
  trainingAidDevelopment: boolean;
  trainingAidMaintenance: boolean;
  trainingMaterialPreparation: boolean;
  trainingMaterialUpdate: boolean;
  documentationWork: boolean;
  travel: boolean;
  others: boolean;
  leave: boolean;
}

interface TableRow {
  id: number;
  description: string;
  date: string;
  duration?: number;
  trainingAidName?: string;
  courseName?: string;
  trainingAidId?: number;
  courseId?: number;
  leaveType?: string;
}

const InstructorActivityTimeTracking = () => {
  const dispatch = useAppDispatch();
  const { activities } = useAppSelector(state => state.activityTracking);
  const navigate = useNavigate();

  const [expandedCategories, setExpandedCategories] =
    useState<ExpandedCategories>({
      selfLearning: false,
      trainingAidDevelopment: false,
      trainingAidMaintenance: false,
      trainingMaterialPreparation: false,
      trainingMaterialUpdate: false,
      documentationWork: false,
      travel: false,
      others: false,
      leave: false,
    });
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState({
    selfLearning: [],
    trainingAidDevelopment: [],
    trainingAidMaintenance: [],
    trainingMaterialPreparation: [],
    trainingMaterialUpdate: [],
    documentationWork: [],
    travel: [],
    others: [],
    leave: [],
  });
  const [startDate, setStartDate] = useState<Date>(subMonths(new Date(), 4));
  const [endDate, setEndDate] = useState<Date>(new Date());

  useEffect(() => {
    dispatch(getTrainerActivities());
  }, [dispatch]);

  const groupActivitiesByCategory = (activities: any[]) => {
    const categories = {
      selfLearning: [],
      trainingAidDevelopment: [],
      trainingAidMaintenance: [],
      trainingMaterialPreparation: [],
      trainingMaterialUpdate: [],
      documentationWork: [],
      travel: [],
      others: [],
      leave: [],
    };

    activities.forEach(activity => {
      const uiCategory = activityCategoryMap[activity.activity_category];
      if (uiCategory && categories[uiCategory]) {
        const mappedActivity = {
          id: activity.id,
          description: activity.description,
          date: activity.activity_date,
          duration: activity.duration,
          trainingAidName: activity.resource_name,
          courseName: activity.course_name,
          trainingAidId: activity.training_aid_id,
          courseId: activity.course_id,
          leaveType: activity.leave_type,
        };
        categories[uiCategory].push(mappedActivity);
      }
    });
    return categories;
  };

  useEffect(() => {
    const grouped = groupActivitiesByCategory(activities);
    const filtered = Object.entries(grouped).reduce(
      (acc, [key, items]) => {
        if (Array.isArray(items)) {
          acc[key] = (items as TableRow[]).filter((item: TableRow) => {
            const matchesSearch =
              searchTerm === "" ||
              (typeof item.description === "string" &&
                item.description
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase())) ||
              (typeof item.trainingAidName === "string" &&
                item.trainingAidName
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase())) ||
              (typeof item.courseName === "string" &&
                item.courseName
                  .toLowerCase()
                  .includes(searchTerm.toLowerCase()));

            const itemDate = new Date(item.date);
            const matchesDateRange =
              itemDate >= startDate && itemDate <= endDate;

            return matchesSearch && matchesDateRange;
          });
        }
        return acc;
      },
      {
        selfLearning: [],
        trainingAidDevelopment: [],
        trainingAidMaintenance: [],
        trainingMaterialPreparation: [],
        trainingMaterialUpdate: [],
        documentationWork: [],
        travel: [],
        others: [],
        leave: [],
      }
    );
    setFilteredData(filtered);
  }, [activities, searchTerm, startDate, endDate]);

  const toggleCategory = (category: keyof ExpandedCategories) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const expandAll = () => {
    setExpandedCategories({
      selfLearning: true,
      trainingAidDevelopment: true,
      trainingAidMaintenance: true,
      trainingMaterialPreparation: true,
      trainingMaterialUpdate: true,
      documentationWork: true,
      travel: true,
      others: true,
      leave: true,
    });
  };

  const onNewEntry = () => {
    navigate(`${process.env.PUBLIC_URL}/log-time/add-time`);
  };

  const collapseAll = () => {
    setExpandedCategories({
      selfLearning: false,
      trainingAidDevelopment: false,
      trainingAidMaintenance: false,
      trainingMaterialPreparation: false,
      trainingMaterialUpdate: false,
      documentationWork: false,
      travel: false,
      others: false,
      leave: false,
    });
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
  };

  const getCommonColumns = () => {
    const columns = [
      {
        name: ActivityOn,
        selector: (row: TableRow) => row.date,
        sortable: true,
        format: (row: TableRow) => formatCustomDate(row.date),
      },
      {
        name: Description,
        cell: (row: TableRow) =>
          createTooltipCell(row.description, `description-${row.id}`),
        sortable: true,
      },
    ];
    columns.push({
      name: DurationInMinutes,
      cell: row => <div>{row.duration}</div>,
      sortable: true,
    });

    return columns;
  };

  const getActionColumn = (title: string) => ({
    name: Action,
    cell: (row: TableRow) => (
      <div className="d-flex gap-2">
        <MUIIcons
          iconName="EditOutlined"
          className="primary-icon-color pointer"
          size={24}
          onClick={() => handleEdit(row, title)}
        />
        <MUIIcons
          iconName="DeleteOutlineOutlined"
          className="primary-icon-color pointer"
          size={24}
          onClick={() => handleDelete(row)}
        />
      </div>
    ),
    width: "100px",
  });

  const getTrainingAidMaintenanceColumns = (title: string) => {
    const additionalColumns = [
      {
        name: TrainingAidMaintenance,
        cell: (row: TableRow) =>
          createTooltipCell(row.trainingAidName, `training-aid-name-${row.id}`),

        sortable: true,
      },
    ];
    return [
      ...getCommonColumns(),
      ...additionalColumns,
      getActionColumn(title),
    ];
  };

  const getTrainingMaterialColumns = (title: string) => {
    const additionalColumns = [
      {
        name: CourseName,
        cell: (row: TableRow) =>
          createTooltipCell(row.courseName, `course-name-${row.id}`),

        sortable: true,
      },
    ];
    return [
      ...getCommonColumns(),
      ...additionalColumns,
      getActionColumn(title),
    ];
  };

  const getLeaveColumns = (title: string) => {
    const additionalColumns = [
      {
        name: LeaveType,
        selector: (row: TableRow) =>
          leaveTypes.find(item => item.value === row.leaveType)?.label || "",
        sortable: true,
      },
    ];
    return [
      ...getCommonColumns(),
      ...additionalColumns,
      getActionColumn(title),
    ];
  };

  const getDefaultColumns = (title: string) => [
    ...getCommonColumns(),
    getActionColumn(title),
  ];

  const handleEdit = (row: TableRow, category: string) => {
    navigate(`${process.env.PUBLIC_URL}/log-time/add-time`, {
      state: {
        row: {
          ...row,
          category: activityCategoryOptions.find(opt => opt.label === category)
            ?.value,
        },
      },
    });
  };

  const handleDelete = (row: TableRow) => {
    dispatch(deleteTrainerActivity(row.id)).then(() => {
      dispatch(getTrainerActivities());
    });
  };

  interface CategorySectionProps {
    title: string;
    data: TableRow[];
  }

  const CategorySection = ({ title, data }: CategorySectionProps) => {
    const getCategoryKey = (
      title: string
    ): keyof ExpandedCategories | undefined => {
      const normalizedTitle = title.trim().toLowerCase();

      const found = activityCategoryOptions.find(
        option => option.label.trim().toLowerCase() === normalizedTitle
      );

      if (!found) return undefined;

      const key =
        activityCategoryMap[found.value as keyof typeof activityCategoryMap];
      return key as keyof ExpandedCategories;
    };

    const categoryKey = getCategoryKey(title);

    // Choose columns based on title
    let columns;
    if (title === TrainingAidMaintenance) {
      columns = getTrainingAidMaintenanceColumns(title);
    } else if (
      title === TrainingMaterialPreparation ||
      title === TrainingMaterialUpdate
    ) {
      columns = getTrainingMaterialColumns(title);
    } else if (title === Leave) {
      columns = getLeaveColumns(title);
    } else {
      columns = getDefaultColumns(title);
    }

    return (
      <Card className="category-section">
        <div
          className="category-section__header"
          onClick={() => toggleCategory(categoryKey)}
        >
          <div className="category-section__title-container">
            <H5 className="category-section__title">{title}</H5>
            <span className="category-section__count">{data.length}</span>
          </div>
          {expandedCategories[categoryKey] ? (
            <ExpandLessIcon />
          ) : (
            <ExpandMoreIcon />
          )}
        </div>
        {expandedCategories[categoryKey] && (
          <div className="category-section__content">
            <DataTable
              columns={columns}
              data={data}
              pagination
              highlightOnHover
              responsive
            />
          </div>
        )}
      </Card>
    );
  };

  return (
    <div className="page-body">
      <div className="container-fluid">
        <div className="track-time">
          <div className="track-time-header">
            <Row className="g-3 align-items-end">
              <Col md={4}>
                <Row className="g-2">
                  <Col md={6}>
                    <FormGroup className="mb-0">
                      <LabelTooltip
                        label={ActivityFrom}
                        tooltipText={ActivityFrom}
                        important={false}
                      />

                      <DatePicker
                        placeholderText={`${Select} ${ActivityFrom}`}
                        className="form-control"
                        dateFormat="dd-MM-yyyy"
                        disabledKeyboardNavigation={true}
                        onKeyDown={(e: React.KeyboardEvent) =>
                          e.preventDefault()
                        }
                        peekNextMonth
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        selected={startDate}
                        onChange={(date: Date) => setStartDate(date)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup className="mb-0">
                      <LabelTooltip
                        label={ActivityTo}
                        tooltipText={ActivityTo}
                        important={false}
                      />

                      <DatePicker
                        placeholderText={`${Select} ${ActivityTo}`}
                        className="form-control"
                        dateFormat="dd-MM-yyyy"
                        disabledKeyboardNavigation={true}
                        onKeyDown={(e: React.KeyboardEvent) =>
                          e.preventDefault()
                        }
                        peekNextMonth
                        showMonthDropdown
                        showYearDropdown
                        dropdownMode="select"
                        selected={endDate}
                        onChange={(date: Date) => setEndDate(date)}
                        minDate={startDate}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </Col>
              <Col md={4}>
                <FormGroup className="mb-0">
                  <LabelTooltip
                    label={Search}
                    tooltipText={Search}
                    important={false}
                  />
                  <div className="track-time-header__search-container">
                    <SearchIcon className="track-time-header__search-icon" />
                    <Input
                      type="text"
                      placeholder={SearchAllItems}
                      value={searchTerm}
                      onChange={handleSearch}
                      className="track-time-header__search-input"
                    />
                    {searchTerm && (
                      <ClearIcon
                        className="track-time-header__clear-icon cursor-pointer"
                        onClick={handleClearSearch}
                      />
                    )}
                  </div>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup className="mb-0 d-flex gap-2 justify-content-end">
                  <SecondaryButton onClick={expandAll}>
                    {ExpandAll}
                  </SecondaryButton>
                  <SecondaryButton onClick={collapseAll}>
                    {CollapseAll}
                  </SecondaryButton>
                  <Btn color="primary" onClick={onNewEntry}>
                    {Add}
                  </Btn>
                </FormGroup>
              </Col>
            </Row>
          </div>

          <CategorySection
            title={SelfLearning}
            data={filteredData.selfLearning}
          />
          <CategorySection
            title={TrainingAidDevelopment}
            data={filteredData.trainingAidDevelopment}
          />
          <CategorySection
            title={TrainingAidMaintenance}
            data={filteredData.trainingAidMaintenance}
          />
          <CategorySection
            title={TrainingMaterialPreparation}
            data={filteredData.trainingMaterialPreparation}
          />
          <CategorySection
            title={TrainingMaterialUpdate}
            data={filteredData.trainingMaterialUpdate}
          />
          <CategorySection
            title={DocumentationWork}
            data={filteredData.documentationWork}
          />
          <CategorySection title={Travel} data={filteredData.travel} />
          <CategorySection title={Others} data={filteredData.others} />
          <CategorySection title={Leave} data={filteredData.leave} />
        </div>
      </div>
    </div>
  );
};

export default InstructorActivityTimeTracking;
